import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, Download, Upload, FileText, Wrench, Flame, Cable, Edit, ChevronLeft, ChevronRight, Package, MoreHorizontal, Boxes, Droplets, Gauge, Activity, Settings } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../../../../components/ui/custom-dropdown-menu';
import {
  FABRIC_BENDS_FLANGES_MATERIAL_TAB_KEYS,
  isBendsFlangesLayoutFabricSubType,
} from './materialCategoryConstants';
import type { DashboardProject } from '../../../types/project';
import type { Materials, MaterialData } from '../../../types/materials';
import { Notification } from '../../../../../components/ui/custom-notification';
import { AddMaterialDialog } from './AddMaterialDialog';
import { UploadMaterialsDialog } from './UploadMaterialsDialog';
import { UploadBatchMaterialDocumentModal } from './UploadBatchMaterialDocumentModal';
import { MaterialReportModal } from './MaterialReportModal';
import { MaterialReportsViewModal } from './MaterialReportsViewModal';
import { GenerateReportButton } from '../../../../../components/ui/GenerateReportButton';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { MediaViewer } from '../../../../../components/viewers/MediaViewer';
import { DocumentViewer } from '../../../../../components/viewers/DocumentViewer';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { cn } from '../../../../../lib/utils';
import { materialsList, pipeSizes } from '../../../mocks/material';
import { downloadMaterialTemplate, processMaterialUpload } from '../../../../../lib/materialUtils';
import { DocumentTag, PipeSize } from '@/features/project/types';
import { PipeTab, makePipes } from './tabs/PipeTab';
import { ForgeTab } from './tabs/ForgeTab';
import { GasTab } from './tabs/GasTab';
import { WireTab } from './tabs/WireTab';
import { CoatingTab } from './tabs/CoatingTab';
import { MiscTab } from './tabs/MiscTab';
import { NDEConsumableTable } from './tabs/NDEConsumableTable';
import { ProjectLimitsModal } from './pipeMillReport/components/ProjectLimitsModal';
import { HeatLotViewModal } from './pipeMillReport/components/HeatLotViewModal';
import { PipeDashboardModal } from './pipeMillReport/components/PipeDashboardModal';
import { PipeMaterialReportModal } from './pipeMillReport/PipeMaterialReportModal';
import { pipeMillReportApi } from '../../../../../redux/Project/pipeMillReport/api';
import { GetProjectMaterialOverview, getMaterialsTemplate } from '../../../../../redux/Project/materialSaga';
import { getAllPipeDetails } from '../../../../../redux/Project/setupData/action';
import { getListMaterialCategory, getAllMaterials } from '../../../../../redux/Project/materialsData/action';
import { getUnitLabel } from '../../../../../utils/unitUtils';
import { useDispatch, useSelector } from 'react-redux';
import { useUserPermissionsAfterLogin } from '../../../../admin/hooks/useUserPermissionsAfterLogin';

interface ProjectMaterialsProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
}

const MATERIAL_CATEGORIES = {
  'Pipe': 'Pipes',
  'Forge': 'Forges',
  'Gas': 'Gas Cylinders',
  'Wire Lot': 'Wire Lots',
  'Coating': 'Coating',
  'Misc': 'Miscellaneous'
} as const;

type MaterialTabKey =
  | 'overview'
  | 'pendingReview'
  | 'pipe'
  | 'Bends'
  | 'Flanges'
  | 'hydro'
  | 'torquing'
  | 'gauging'
  | 'flushing'
  | 'forge'
  | 'gas'
  | 'wire'
  | 'coating'
  | 'nde'
  | 'misc';

const FABRIC_BENDS_FLANGES_TAB_SET = new Set<string>(FABRIC_BENDS_FLANGES_MATERIAL_TAB_KEYS);

export const ProjectMaterials: React.FC<ProjectMaterialsProps> = ({ project, canEdit = true, canReport = true }): JSX.Element => {
  const dispatch = useDispatch();
  const { userPermissions, isAdminOrHigher } = useUserPermissionsAfterLogin();

  const isCustomerRepresentativeUser = useMemo(() => {
    return (project.project as any)?.team_member_role === 'Customer Representative';
  }, [project]);

  const isQCInspectorUser = useMemo(() => {
    return (
      (project.project as any)?.team_member_role === 'QC Inspector' ||
      (project.project as any)?.team_member_role === 'Quality Control' ||
      (project.project as any)?.team_member_role === 'QA/QC'
    );
  }, [project]);

  const isWelderUser = useMemo(() => {
    return (
      (project.project as any)?.team_member_role === 'Welder' ||
      (project.project as any)?.team_member_role === 'Welding'
    );
  }, [project]);

  const currentUserTeamMemberId = useMemo(() => userPermissions?.user_id ?? null, [userPermissions]);

  const isAdminOrSuperAdmin = useMemo(() => isAdminOrHigher(), [isAdminOrHigher]);
  const { pipeDetails, isPipeDetailsLoading } = useSelector((state: any) => state.setupData || {});
  const { materialCategories, materials: materialsCache, isMaterialCategoryLoading, isMaterialsLoading } = useSelector((state: any) => state.materialsData || {});

  const [materials, setMaterials] = useState<MaterialData[]>([]);
  const [isAddingMaterial, setIsAddingMaterial] = useState(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);
  const [selectedSize, setSelectedSize] = useState<string>('0');
  const [materialTab, setMaterialTab] = useState<MaterialTabKey>('overview');
  const [materialsPerPage, setMaterialsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pipeSize, setPipeSize] = useState<PipeSize[]>(pipeSizes);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [setupData, setSetupData] = useState<any>({});
  const [specifications, setSpecifications] = useState<any[]>([]);
  const [specificationTab, setSpecificationTab] = useState<'pipeline' | 'fabrication' | 'others'>('pipeline');
  const [pipeSizesData, setPipeSizesData] = useState<any[]>([]);
  const [selectedPipe, setSelectedPipe] = useState<any>(null);
  const [unit, setUnit] = useState<string>(project.project.unit || 'in');
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  // Add state for tracking individual row deletions
  const [deletingRows, setDeletingRows] = useState<Set<string>>(new Set());
  const [lastPipeSizeSelection, setLastPipeSizeSelection] = useState<number | null>(null);
  const [lastProjectId, setLastProjectId] = useState<string | number | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isUploadBatchMaterialDialogOpen, setIsUploadBatchMaterialDialogOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReportsViewModalOpen, setIsReportsViewModalOpen] = useState(false);
  const [isProjectLimitsModalOpen, setIsProjectLimitsModalOpen] = useState(false);
  const [isPipeDashboardOpen, setIsPipeDashboardOpen] = useState(false);
  const [pipeDashboardItems, setPipeDashboardItems] = useState<any[]>([]);
  const [pipeDashboardFrequencyCoverage, setPipeDashboardFrequencyCoverage] = useState<any[]>([]);
  const [isPipeDashboardLoading, setIsPipeDashboardLoading] = useState(false);
  const [isHeatLotViewOpen, setIsHeatLotViewOpen] = useState(false);
  const [heatLotItems, setHeatLotItems] = useState<any[]>([]);
  const [isHeatLotLoading, setIsHeatLotLoading] = useState(false);
  const [projectMaterialOverview, setProjectMaterialOverview] = useState<any>(null);
  const [isProjectOverviewLoading, setIsProjectOverviewLoading] = useState(false);
  const [dashboardReportMaterial, setDashboardReportMaterial] = useState<MaterialData | null>(null);
  const [isDashboardReportOpen, setIsDashboardReportOpen] = useState(false);
  const [dashboardInitialReportType, setDashboardInitialReportType] = useState<string | null>(null);
  const [documentTags, setDocumentTags] = useState<any[]>([]);

  // Viewer state management
  const [viewerType, setViewerType] = useState<'pdf' | 'media' | 'document' | null>(null);
  const [viewerFile, setViewerFile] = useState<any | null>(null);
  const [viewerFiles, setViewerFiles] = useState<any[]>([]);

  //test project for pipe mill project

const isPipemillproject = project?.project?.projectName === 'Demo Project - Q' && project?.project?.projectNumber === 'PQ1245';

  useEffect(() => {
    // const fetchSettings = async () => {
    //   const settings = localStorage.getItem('settings');
    //   if (settings) {
    //     const settingsData = JSON.parse(settings);

    //     setUnit(getUnitLabel(settingsData.general.unit));
    //   }
    //   else {
    //     setUnit(getUnitLabel('Inches'));
    //   }
    // };
    // fetchSettings();
    if (project.project.unit) {
      setUnit(project.project.unit || 'in');
    }
  }, [project.project.unit]);

  const getCoating = (type: string, subType: string) => {
    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.coating?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.coating?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.coating?.enabled;
    }
    else if (type === 'fabrication' && subType === 'spools') {
      return project.project.projectTypes.fabrication?.options?.spools?.features?.coating?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.coating?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.coating?.enabled;
    }

    return false;
  }
  const isEnabledMaterialTracing = (type: string, subType: string) => {

    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.materialTraceability?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.materialTraceability?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.materialTraceability?.enabled;
    }
    else if (type === 'fabrication' && subType === 'spools') {
      return project.project.projectTypes.fabrication?.options?.spools?.features?.materialTraceability?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.materialTraceability?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.materialTraceability?.enabled;
    }

    return false;
  }
  const isEnabledPipefitter = (type: string, subType: string) => {

    if (type === 'fabrication' && subType === 'buckleArrestor') {
      return project.project.projectTypes.fabrication?.options?.buckleArrestor?.features?.pipeFitter?.enabled;
    }
    else if (type === 'fabrication' && subType === 'pipeInPipe') {
      return project.project.projectTypes.fabrication?.options?.pipeInPipe?.features?.pipeFitter?.enabled;
    }
    else if (type === 'fabrication' && subType === 'bulkhead') {
      return project.project.projectTypes.fabrication?.options?.bulkhead?.features?.pipeFitter?.enabled;
    }
    else if (type === 'fabrication' && subType === 'spools') {
      return project.project.projectTypes.fabrication?.options?.spools?.features?.pipeFitter?.enabled;
    }
    else if (type === 'pipeline' && subType === 'none') {
      return project.project.projectTypes.pipeline?.features?.pipeFitter?.enabled;
    }
    else if (type === 'others' && subType === 'none') {
      return project.project.projectTypes.others?.features?.pipeFitter?.enabled;
    }

    return false;
  }

  // Update document tags when materialCategories changes from Redux
  useEffect(() => {
    if (materialCategories && materialCategories.length > 0) {
      // Remove duplicates and sort tags alphabetically by tags_name
      const uniqueTags = materialCategories
        .filter((tag: any, index: number, self: any[]) =>
          index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
        )
        .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

      setDocumentTags(uniqueTags);
    }
  }, [materialCategories]);

  // Function to extract pipe sizes from setup data
  const extractPipeSizes = useCallback((setupData: any): any[] => {
    const pipeSizes: any[] = [];

    Object.values(setupData).forEach((setup: any) => {
      const coating = getCoating(setup.projectType, setup.projectSubType);
      const materialTraceability = isEnabledMaterialTracing(setup.projectType, setup.projectSubType);
      const pipeFitter = isEnabledPipefitter(setup.projectType, setup.projectSubType);
      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          const pipeName = pipe.pipeName || `Pipe ${pipe.pipeOrder || pipe.pipeId || ''}`.trim();
          const pipeSize = pipe.pipeSize || pipe.size || 0;
          const wallThickness = pipe.wallThickness || 0;
          const pipeData = {
            pipeId: pipe.pipeId,
            pipeName,
            projectSubType: setup.projectSubType,
            projectType: setup.projectType,
            projectId: setup.projectId,
            projectTypeId: setup.projectTypeId,
            size: pipeSize,
            wallThickness,
            manufacturers: pipe.manufacturers || [],
            coating: coating,
            materialTraceability: materialTraceability,
            pipeFitter: pipeFitter,
            displayName: setup.projectSubType === 'none' && setup.projectType !== 'fabrication' ?
              `${setup.projectType === 'pipeline' ? 'Pipeline' : setup.projectType === 'others' ? 'Others' : setup.projectType}  ${pipe.pipeName} (${pipe.pipeSize}" ×  ${pipe.wallThickness}")` :
              `${setup.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' :
                setup.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' :
                  setup.projectSubType === 'bulkhead' ? 'Bulk Head' :
                    setup.projectSubType === 'spools' ? 'Spools' :
                    setup.projectSubType}  ${pipe.pipeName} (${pipe.pipeSize}"  × ${pipe.wallThickness}")`
          };
          pipeSizes.push(pipeData);
        });
      }
    });

    return pipeSizes;
  }, [project.project.projectTypes]);

  // Memoize extracted pipe sizes to prevent unnecessary re-renders
  const extractedPipeSizes = useMemo(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      return extractPipeSizes(pipeDetails);
    }
    return [];
  }, [pipeDetails, extractPipeSizes]);

  // Update pipe sizes when pipeDetails changes from Redux
  useEffect(() => {
    if (extractedPipeSizes.length > 0) {
      setPipeSizesData(extractedPipeSizes);
      if (extractedPipeSizes[selectedPipeSize]) {
        setSelectedPipe(extractedPipeSizes[selectedPipeSize]);
      }
    }
  }, [ pipeDetails, extractedPipeSizes, selectedPipeSize]);

  // Update materials when materialsCache changes from Redux
  useEffect(() => {
    if (pipeSizesData && pipeSizesData.length > 0 && pipeSizesData[selectedPipeSize]) {
      const cacheKey = `${project.project.projectId}_${pipeSizesData[selectedPipeSize].projectTypeId}_${pipeSizesData[selectedPipeSize].pipeId}`;
      const cachedMaterials = materialsCache?.[cacheKey];
      
      if (cachedMaterials) {
        setSetupData(cachedMaterials);
      } else if (materialsCache && !cachedMaterials && !isMaterialsLoading) {
        // If cache exists but this key doesn't, and we're not loading, set empty data
        // This prevents infinite loading when no materials are available
        setSetupData({});
      }
    }
  }, [materialsCache, pipeSizesData, selectedPipeSize, project.project.projectId, isMaterialsLoading]);

  // Update loading states from Redux
  useEffect(() => {
    // Check if any data is loading
    setIsLoadingData(isMaterialCategoryLoading || isPipeDetailsLoading);
  }, [isMaterialCategoryLoading, isPipeDetailsLoading]);

  useEffect(() => {
    setTableDataLoading(isMaterialsLoading);
  }, [isMaterialsLoading]);

  // Load initial data - only fetch if project ID changed or pipeDetails doesn't match current project
  useEffect(() => {
    const currentProjectId = project.project.projectId;
    
    // Check if project ID changed
    const projectIdChanged = lastProjectId !== null && lastProjectId !== currentProjectId;
   
    // Check if pipeDetails exists and matches current project ID
    const pipeDetailsMatchesProject = pipeDetails && Object.keys(pipeDetails).length > 0 && 
      Object.values(pipeDetails).some((setup: any) => 
        setup.projectId && String(setup.projectId) === String(currentProjectId)
      );

    // Only fetch pipeDetails if:
    // 1. Project ID changed (need to fetch new project's data)
    // 2. pipeDetails is null/undefined/empty
    // 3. pipeDetails exists but doesn't match current project ID
    if (currentProjectId) {
      if (projectIdChanged || !pipeDetails || Object.keys(pipeDetails).length === 0 || !pipeDetailsMatchesProject) {
        dispatch(getAllPipeDetails({ projectId: currentProjectId }));
       // dispatch(getListMaterialCategory({}));
      }
    }
    
    // Update last project ID
    if (currentProjectId) {
      setLastProjectId(currentProjectId);
    }
  }, [project.project.projectId]);

  // Fetch material categories separately (doesn't depend on project ID)
  useEffect(() => {
  //  console.log('materialCategories', materialCategories);
    // Only fetch material categories if null/undefined/empty (this doesn't depend on project ID)
    if (!materialCategories || materialCategories.length === 0) {
      dispatch(getListMaterialCategory({}));
    }
  }, [materialCategories, dispatch]);


  const refreshProjectOverview = useCallback(async () => {
    const projectId = Number(project.project.projectId);
    if (!projectId) return;

    setIsProjectOverviewLoading(true);
    try {
      const data = await GetProjectMaterialOverview(projectId);
      setProjectMaterialOverview(data);
    } finally {
      setIsProjectOverviewLoading(false);
    }
  }, [project.project.projectId]);

  const refreshMaterials = useCallback(async () => {
    if (!pipeSizesData || !pipeSizesData[selectedPipeSize]) return;
    
    // Force refresh materials from API after add/delete/upload
    const payload = {
      project_id: project.project.projectId,
      project_type_id: pipeSizesData[selectedPipeSize].projectTypeId,
      pipe_id: pipeSizesData[selectedPipeSize].pipeId
    };
    dispatch(getAllMaterials(payload));
    await refreshProjectOverview();
    // The useEffect will automatically update when materialsCache changes
  }, [pipeSizesData, selectedPipeSize, project.project.projectId, dispatch, refreshProjectOverview]);

  // Helper function to fetch materials for a specific pipe size index
  const fetchMaterialsForPipeSize = useCallback(async (pipeSizeIndex: number) => {
    if (!pipeSizesData || !pipeSizesData[pipeSizeIndex]) return;

    const payload = {
      project_id: project.project.projectId,
      project_type_id: pipeSizesData[pipeSizeIndex].projectTypeId,
      pipe_id: pipeSizesData[pipeSizeIndex].pipeId
    };
    dispatch(getAllMaterials(payload));
    // Check if materials are already cached
   
    
  }, [pipeSizesData]);

  // // // Optimized refresh function that only loads when pipe size changes
  // const refreshMaterialsOnPipeSizeChange = useCallback(async () => {
  //   await fetchMaterialsForPipeSize(selectedPipeSize);
  // }, [selectedPipeSize]);

  // Function to handle row deletion without full table refresh
  const handleRowDelete = useCallback(async (materialId: string | number, category: string) => {
    const materialIdStr = String(materialId);
    setDeletingRows(prev => new Set(Array.from(prev).concat([materialIdStr])));

    try {
      // Import deleteMaterials function
      const { deleteMaterials } = await import('../../../../../redux/Project/materialSaga');
      const res = await deleteMaterials([materialIdStr], category);

      if (res) {
     
        // Update the local state by removing the deleted item
        setSetupData((prevData: any) => {
          const updatedData = { ...prevData };

          // Remove from the appropriate category array
          if (category === 'Pipe' && updatedData.pipes) {
            updatedData.pipes = updatedData.pipes.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Bends' && updatedData.bends) {
            updatedData.bends = updatedData.bends.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Flanges' && updatedData.flanges) {
            updatedData.flanges = updatedData.flanges.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Hydro' && updatedData.hydro) {
            updatedData.hydro = updatedData.hydro.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Torquing' && updatedData.torquing) {
            updatedData.torquing = updatedData.torquing.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Gauging' && updatedData.gauging) {
            updatedData.gauging = updatedData.gauging.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Flushing' && updatedData.flushing) {
            updatedData.flushing = updatedData.flushing.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Forge' && updatedData.forge) {
            updatedData.forge = updatedData.forge.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Gas' && updatedData.gas) {
            updatedData.gas = updatedData.gas.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Wire Lot' && updatedData.wireLot) {
            updatedData.wireLot = updatedData.wireLot.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Coating' && updatedData.coating) {
            updatedData.coating = updatedData.coating.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Misc' && updatedData.misc) {
            updatedData.misc = updatedData.misc.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'Misc' && updatedData.miscellaneous) {
            updatedData.miscellaneous = updatedData.miscellaneous.filter((item: any) => String(item.materialId) !== materialIdStr);
          } else if (category === 'NDE' && updatedData.nde) {
            updatedData.nde = updatedData.nde.filter((item: any) => String(item.materialId) !== materialIdStr);
          }

          return updatedData;
        });
           // Force refresh materials from API after delete
          await refreshMaterials();

      }
    } catch (error) {
    } finally {
      setDeletingRows(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(materialIdStr);
        return newSet;
      });
    }
  }, [refreshMaterials, refreshProjectOverview]);

  // Load materials when pipe sizes are set (after pipeDetails is loaded)
  useEffect(() => {
    if (pipeSizesData && pipeSizesData.length > 0 && pipeSizesData[selectedPipeSize]) {
      fetchMaterialsForPipeSize(selectedPipeSize);
    }
  }, [pipeSizesData, selectedPipeSize, fetchMaterialsForPipeSize]);

  useEffect(() => {
    refreshProjectOverview();
  }, [refreshProjectOverview]);


  // Set initial tab based on project type
  useEffect(() => {
    if (pipeSizesData[selectedPipeSize]) {
      setMaterialTab('overview');
    }
  }, [pipeSizesData, selectedPipeSize]);

  const getMaterials = (type: string) => {
    if (type === 'bends') {
      return setupData?.bends || [];
    }
    if (type === 'flanges') {
      return setupData?.flanges || [];
    }
    if (type === 'hydro') {
      return setupData?.hydro || [];
    }
    if (type === 'torquing') {
      return setupData?.torquing || [];
    }
    if (type === 'gauging') {
      return setupData?.gauging || [];
    }
    if (type === 'flushing') {
      return setupData?.flushing || [];
    }
    if (type === 'pipe') {
      return setupData?.pipes || [];
    }
    else if (type === 'forge') {
      return setupData?.forge || [];
    }
    else if (type === 'gas') {
      return setupData?.gas || [];
    }
    else if (type === 'wire') {
      return setupData?.wireLot || [];
    }
    else if (type === 'coating') {
      return setupData?.coating || [];
    }
    else if (type === 'misc') {
      return setupData?.misc ?? setupData?.miscellaneous ?? [];
    }
    else if (type === 'nde') {
      return setupData?.nde || [];
    }
    return [];
  }

  const isMaterialTabVisible = useCallback((key: MaterialTabKey) => {
    if (key === 'overview' || key === 'pendingReview') return true;
    if (!selectedPipe) return true;

    const projectType = String(selectedPipe.projectType || '').toLowerCase();
    const commonTabs = ['pipe', 'forge', 'gas', 'wire', 'nde', 'misc'];

    if (projectType === 'pipeline') {
      return key === 'pipe' || key === 'misc' || Boolean(selectedPipe.coating && key === 'coating');
    }

    if (projectType === 'others') {
      return commonTabs.includes(key) || Boolean(selectedPipe.coating && key === 'coating');
    }

    if (projectType === 'fabrication') {
      if (isBendsFlangesLayoutFabricSubType(selectedPipe.projectSubType)) {
        return FABRIC_BENDS_FLANGES_TAB_SET.has(key) || Boolean(selectedPipe.coating && key === 'coating');
      }
      return commonTabs.includes(key) || Boolean(selectedPipe.coating && key === 'coating');
    }

    return true;
  }, [selectedPipe]);

  const projectOverviewCategoryCounts = useMemo(() => (
    (Array.isArray(projectMaterialOverview?.categories) ? projectMaterialOverview.categories : []).reduce((acc: Record<string, any>, category: any) => {
      if (category?.key) {
        acc[category.key] = category;
      }
      return acc;
    }, {})
  ), [projectMaterialOverview]);

  const materialOverviewCategories = useMemo(() => {
    const pipeLabel = pipeSizesData[selectedPipeSize]?.projectType === 'pipeline'
      ? 'Base Pipes'
      : pipeSizesData[selectedPipeSize]?.projectType === 'fabrication'
        ? 'Base Pipes'
        : 'Pipes';

    const categories = [
      { key: 'pipe' as MaterialTabKey, dataKey: 'pipe', label: pipeLabel, icon: FileText, description: 'SAWL pipe scope', tone: 'border-blue-100 bg-white' },
      { key: 'Bends' as MaterialTabKey, dataKey: 'bends', label: 'Bends', icon: FileText, description: 'bend items', tone: 'border-slate-100 bg-white' },
      { key: 'Flanges' as MaterialTabKey, dataKey: 'flanges', label: 'Flanges', icon: FileText, description: 'flange items', tone: 'border-slate-100 bg-white' },
      { key: 'hydro' as MaterialTabKey, dataKey: 'hydro', label: 'Hydro', icon: Droplets, description: 'hydro records', tone: 'border-sky-100 bg-white' },
      { key: 'torquing' as MaterialTabKey, dataKey: 'torquing', label: 'Torquing', icon: Wrench, description: 'torque records', tone: 'border-slate-100 bg-white' },
      { key: 'gauging' as MaterialTabKey, dataKey: 'gauging', label: 'Gauging', icon: Gauge, description: 'gauge records', tone: 'border-slate-100 bg-white' },
      { key: 'flushing' as MaterialTabKey, dataKey: 'flushing', label: 'Flushing', icon: Activity, description: 'flush records', tone: 'border-slate-100 bg-white' },
      { key: 'forge' as MaterialTabKey, dataKey: 'forge', label: 'Forging', icon: Wrench, description: 'forgings', tone: 'border-slate-100 bg-white' },
      { key: 'gas' as MaterialTabKey, dataKey: 'gas', label: 'Gas', icon: Flame, description: 'shielding', tone: 'border-orange-100 bg-white' },
      { key: 'wire' as MaterialTabKey, dataKey: 'wire', label: 'Wire Lot', icon: Cable, description: 'consumables', tone: 'border-slate-100 bg-white' },
      { key: 'coating' as MaterialTabKey, dataKey: 'coating', label: 'Coating', icon: Cable, description: '3LPE / FBE', tone: 'border-emerald-100 bg-white' },
      { key: 'misc' as MaterialTabKey, dataKey: 'misc', label: 'Misc', icon: Boxes, description: 'sleeves', tone: 'border-slate-100 bg-white' },
    ];

    return categories
      .filter((category) => isMaterialTabVisible(category.key))
      .map((category) => ({
        ...category,
        count: Number(projectOverviewCategoryCounts[category.dataKey]?.total || 0),
      }));
  }, [isMaterialTabVisible, pipeSizesData, selectedPipeSize, projectOverviewCategoryCounts]);

  const overviewStats = useMemo(() => {
    const summary = projectMaterialOverview?.summary || {};
    const total = Number(summary.ingested || 0);
    const consumed = Number(summary.consumed || 0);
    const pending = Number(summary.pending || 0);

    return {
      total,
      reportsComplete: Number(summary.reportsComplete ?? Math.max(total - pending, 0)),
      reviewed: Number(summary.reviewed ?? Math.max(total - pending, 0)),
      cleared: Number(summary.cleared ?? Math.max(total - pending, 0)),
      consumed,
      pending,
    };
  }, [projectMaterialOverview]);

  const pendingReviewQueue = useMemo(() => (
    Array.isArray(projectMaterialOverview?.pendingReviewItems)
      ? projectMaterialOverview.pendingReviewItems
      : []
  ), [projectMaterialOverview]);
  const pendingReviewItems = useMemo(() => pendingReviewQueue.slice(0, 6), [pendingReviewQueue]);

  const heatLotMaterials = useMemo(
    () => [
      ...(setupData?.pipes || []),
      ...(isPipemillproject ? makePipes() : []),
    ],
    [isPipemillproject, setupData?.pipes],
  );

  useEffect(() => {
    if (!isPipeDashboardOpen) return;
    const projectId = Number(project.project.projectId) || undefined;
    if (!projectId) return;

    let isMounted = true;
    setIsPipeDashboardLoading(true);
    pipeMillReportApi.fetchPipeDashboard({ projectId })
      .then((data: any) => {
        if (!isMounted) return;
        const dashboardData = data?.data && typeof data.data === 'object' ? data.data : data;
        const dashboardItems = Array.isArray(dashboardData?.items) ? dashboardData.items : [];
        const frequencyCoverage = Array.isArray(dashboardData?.frequencyCoverage)
          ? dashboardData.frequencyCoverage
          : Array.isArray(dashboardData?.frequency_coverage)
            ? dashboardData.frequency_coverage
            : [];
        setPipeDashboardItems(dashboardItems);
        setPipeDashboardFrequencyCoverage(frequencyCoverage);
      })
      .catch((error: unknown) => {
        if (!isMounted) return;
        setPipeDashboardItems([]);
        setPipeDashboardFrequencyCoverage([]);
        Notification.error('Pipe dashboard', error instanceof Error ? error.message : 'Failed to load dashboard data');
      })
      .finally(() => {
        if (isMounted) setIsPipeDashboardLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isPipeDashboardOpen, project.project.projectId]);

  useEffect(() => {
    if (!isHeatLotViewOpen) return;
    const projectId = Number(project.project.projectId) || undefined;
    if (!projectId) return;

    let isMounted = true;
    setIsHeatLotLoading(true);
    pipeMillReportApi.fetchHeatLotView({ projectId })
      .then((data: any) => {
        if (!isMounted) return;
        setHeatLotItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((error: unknown) => {
        if (!isMounted) return;
        setHeatLotItems([]);
        Notification.error('Heat & Lot', error instanceof Error ? error.message : 'Failed to load heat and lot data');
      })
      .finally(() => {
        if (isMounted) setIsHeatLotLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isHeatLotViewOpen, project.project.projectId]);

  const openDashboardPipeReport = (pipe: any, initialReportType?: string | null) => {
    const material: MaterialData = {
      materialId: pipe.id ?? pipe.pipeId ?? pipe.pipeNo,
      projectId: project.project.projectId,
      pipeId: pipe.pipeId,
      materialCategory: 'Pipe',
      materialStatus: pipe.clearance === 'PASS' ? 'Available' : pipe.clearance === 'FAIL' ? 'Consumed' : 'Shipped to NDT',
      materialDate: new Date().toISOString(),
      details: {
        pipeNRI: pipe.pipeNri || pipe.pipeNRI || `NRI-U-${pipe.pipeNo || pipe.id}`,
        pipeNo: String(pipe.pipeNo || pipe.id || ''),
        pipePipeId: pipe.pipeId ? `PIPE-${pipe.pipeId}` : `PIPE-${pipe.pipeNo || pipe.id}`,
        pipeHeatNumber: pipe.heatNumber || '',
        pipeLength: pipe.pipeLength || 0,
        pipeProjectStage: pipe.projectStage || 'Production',
        pipeExtraDetails: {
          pipeData: {
            lotId: pipe.lotId || 'LOT-UNASSIGNED',
            sequence: pipe.sequence,
            clearance: pipe.clearance,
            passing: pipe.passing,
          },
        },
      },
      documents: [],
    } as MaterialData;

    setDashboardReportMaterial(material);
    setDashboardInitialReportType(initialReportType || null);
    setIsDashboardReportOpen(true);
  };

  const openPendingReviewItem = (item: any, categoryKey: MaterialTabKey) => {
    if (item?.source === 'pipeMillReport') {
      openDashboardPipeReport(
        {
          id: item.pipeId || item.pipeNo || item.id,
          pipeId: item.pipeId,
          pipeNo: item.pipeNo || item.pipeNumber,
          heatNumber: item.heatNumber,
          clearance: item.overallStatus || item.status || 'PENDING_REVIEW',
          lotId: item.lotId || 'LOT-UNASSIGNED',
        },
        item.reportType || item.report_type
      );
      return;
    }

    setMaterialTab(isMaterialTabVisible(categoryKey) ? categoryKey : 'overview');
  };


  const getFilteredSetupData = () => {
    if (!setupData || Object.keys(setupData).length === 0) {
      return {};
    }

    const filteredData: any = {};

    if (specificationTab === 'pipeline') {
      // Filter for pipeline data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'pipeline') {
          filteredData[key] = data;
        }
      });
    } else if (specificationTab === 'fabrication') {
      // Filter for fabrication data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'fabrication') {
          filteredData[key] = data;
        }
      });
    } else if (specificationTab === 'others') {
      // Filter for others data
      Object.entries(setupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'others') {
          filteredData[key] = data;
        }
      });
    }

    return filteredData;
  };

  // useEffect(() => {
  //   const firstAvailableIndex = pipeSize.findIndex((_, index) => {
  //     if (index === 0 && project.project.projectTypes.pipeline?.enabled) return true;
  //     if (index === 1 && project.project.projectTypes.fabrication?.enabled) return true;
  //     return false;
  //   });

  //   if (firstAvailableIndex !== -1) {
  //     setSelectedPipeSize(firstAvailableIndex);
  //     setSelectedSize(firstAvailableIndex.toString());
  //     if (firstAvailableIndex === 0) setMaterialTab('pipe');
  //     if (firstAvailableIndex === 1) setMaterialTab('forge');
  //   }
  // }, [project.project.projectTypes, pipeSize]);

  const handleAddMaterial = async (material: MaterialData) => {
    // setMaterials(prev => [...prev, material]);
    await refreshMaterials();
  };

  const handleImport = () => {
    // TODO: Implement import
  };

  const handleExport = () => {
    setIsReportModalOpen(true);
  };

  const handleGenerateMaterialReport = () => {
    setIsReportsViewModalOpen(true);
  };

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage: number) => {
    setMaterialsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);



  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      await getMaterialsTemplate(project.project.projectId, project.project.projectTypes.pipeline?.project_type_id, "MATERIAL_TRACEABILITY");
      setIsDownloadingTemplate(false)
    } catch (error) {
      //  Notification.error('Failed to download template');
      setIsDownloadingTemplate(false);
    }
  };

  // File type detection utilities
  const getFileExtension = (fileName?: string, fileUrl?: string) => {
    const name = fileName || fileUrl?.split('?')[0].split('/').pop() || '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  const getViewerType = (file: any): 'pdf' | 'media' | 'document' => {
    const ext = getFileExtension(file?.fileName, file?.fileUrl);
    if (ext === 'pdf') return 'pdf';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'tif'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'webm'];
    if (imageExts.includes(ext) || videoExts.includes(ext)) return 'media';
    return 'document'; // Default for office docs and fallback
  };

  // Unified viewer handler
  const openViewer = (fileOrFiles: any | any[]) => {
    if (!fileOrFiles) return;

    // Handle array of files
    if (Array.isArray(fileOrFiles)) {
      const validFiles = fileOrFiles.filter((f: any) => f?.fileUrl || f?.file_url);
      if (validFiles.length === 0) {
        Notification.error('No valid files to view');
        return;
      }

      // Check if all files are media (images/videos)
      const allMedia = validFiles.every((file: any) => getViewerType(file) === 'media');
      
      if (allMedia && validFiles.length > 1) {
        // Multiple media files - use MediaViewer
        setViewerFiles(validFiles);
        setViewerType('media');
        setViewerFile(null);
      } else {
        // Single file or mixed types - open first file
        openViewer(validFiles[0]);
      }
      return;
    }

    // Handle single file
    const file = fileOrFiles;
    if (!file?.fileUrl && !file?.file_url) {
      Notification.error('File URL not available');
      return;
    }

    // Normalize file object to ensure fileUrl exists
    const normalizedFile = {
      ...file,
      fileUrl: file.fileUrl || file.file_url || file.url,
      fileName: file.fileName || file.file_name || file.name
    };

    const type = getViewerType(normalizedFile);
    setViewerType(type);
    
    if (type === 'media') {
      // For media, use array format for MediaViewer
      setViewerFiles([normalizedFile]);
      setViewerFile(null);
    } else {
      // For PDF and documents, use single file
      setViewerFile(normalizedFile);
      setViewerFiles([]);
    }
  };

  const closeViewer = () => {
    setViewerType(null);
    setViewerFile(null);
    setViewerFiles([]);
  };

  const handleUploadComplete = async () => {
    // Refresh materials data after upload
    await refreshMaterials();
  };

  const formatPendingWaiting = (value?: string | null) => {
    if (!value) return '-';
    const updatedAt = new Date(value).getTime();
    if (!Number.isFinite(updatedAt)) return '-';
    const elapsedMs = Date.now() - updatedAt;
    if (elapsedMs < 0) return '-';
    const minutes = Math.floor(elapsedMs / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d${hours % 24 ? ` ${hours % 24}h` : ''}`;
  };

  const TABS_CONFIG = useMemo(() => ({
    overview: {
      label: 'Overview',
      icon: Activity,
      content: () => (
        <div className="space-y-5 bg-[#F7F8FA] p-4 sm:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-heritage-grey">
                Material Traceability
              </div>
              <h3 className="mt-1 text-[22px] font-semibold text-blueprint-blue">Project overview</h3>
              <p className="mt-1 text-[13px] text-heritage-grey">
                {project.project.projectName || 'Selected project'} - {overviewStats.total} material items in project scope
              </p>
              {isProjectOverviewLoading && (
                <p className="mt-1 text-[12px] text-heritage-grey">Refreshing project overview...</p>
              )}
            </div>
            {/* <div className="flex min-w-[260px] items-center rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 shadow-sm">
              <span className="text-[12px] text-heritage-grey">Trace by NRI, heat, lot, wire, or coating batch...</span>
              <Button size="sm" className="ml-auto h-7 bg-hero-red text-white hover:bg-hero-red-60">
                Trace
              </Button>
            </div> */}
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {[
              { label: 'Ingested', value: overviewStats.total, delta: '', tone: 'border-hero-red' },
              { label: 'Reports complete', value: overviewStats.reportsComplete, delta: overviewStats.pending ? `-${overviewStats.pending}` : '', tone: 'border-hero-red' },
              { label: 'Reviewed', value: overviewStats.reviewed, delta: overviewStats.pending ? `-${overviewStats.pending}` : '', tone: 'border-hero-red' },
              { label: 'Cleared', value: overviewStats.cleared, delta: overviewStats.pending ? `-${overviewStats.pending}` : '', tone: 'border-hero-red' },
              { label: 'Consumed', value: overviewStats.consumed, delta: overviewStats.consumed ? `+${overviewStats.consumed}` : '', tone: 'border-emerald-600 bg-emerald-50' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-[#E2E5EA] bg-white p-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">{stat.label}</div>
                <div className="mt-2 flex items-end gap-2">
                  <span className="text-[24px] font-semibold leading-none text-blueprint-blue">{stat.value}</span>
                  {stat.delta && <span className="text-[11px] font-semibold text-hero-red">{stat.delta}</span>}
                </div>
                <div className={cn('mt-3 h-[3px] rounded-full', stat.tone)} />
              </div>
            ))}
          </div>

          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-heritage-grey">
              Material categories
            </div>
            <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
              {materialOverviewCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.key}
                    type="button"
                    onClick={() => setMaterialTab(category.key)}
                    className={cn(
                      'rounded-lg border p-4 text-left shadow-sm transition hover:-translate-y-[1px] hover:border-hero-red hover:shadow-md',
                      category.tone,
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-heritage-grey">{category.label}</div>
                      <Icon className="h-4 w-4 text-heritage-grey" />
                    </div>
                    <div className="mt-3 flex items-end gap-2">
                      <span className="text-[24px] font-semibold leading-none text-blueprint-blue">{category.count}</span>
                      <span className="text-[11px] text-heritage-grey">{category.description}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.3fr_0.9fr]">
            <div className="rounded-xl border border-[#E2E5EA] bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-[#E2E5EA] px-4 py-3">
                <h4 className="text-[14px] font-semibold text-blueprint-blue">Pending review</h4>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700">{overviewStats.pending}</span>
                  <Button type="button" variant="outline" size="sm" className="h-7 border-[#E2E5EA] bg-white text-[11px]" onClick={() => setMaterialTab('pendingReview')}>
                    Open all
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-[#EEF0F3]">
                {pendingReviewItems.length > 0 ? pendingReviewItems.map((item: any) => {
                  const material = item.material || item;
                  const details = material.details || {};
                  const categoryKey = (item.categoryKey || item.key || 'overview') as MaterialTabKey;
                  const categoryLabel = item.categoryLabel || item.category || material.materialCategory || 'Material';
                  const reportType = item.reportType || material.reportType;
                  const heatNumber = item.heatNumber || material.heatNumber || details.pipeHeatNumber;
                  const ref = details.pipeNo || details.pipeNRI || item.pipeNo || item.pipeNumber || material.pipeNo || material.materialId || 'Pending item';
                  const statusText = String(item.overallStatus || material.materialStatus || material.status || item.status || 'Pending review').replace(/_/g, ' ');
                  const reason = item.reason || material.materialStatus || material.status || 'Pending review';
                  return (
                    <button
                      key={`${categoryKey}-${material.materialId || ref}`}
                      type="button"
                      onClick={() => openPendingReviewItem(item, categoryKey)}
                      className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[#F7F8FA]"
                    >
                      <div>
                        <div className="text-[13px] font-semibold text-blueprint-blue">
                          {categoryLabel}
                          {reportType && <span className="ml-2 text-[12px] font-semibold text-hero-red">{String(reportType).replace(/_/g, ' ')}</span>}
                          <span className="ml-2 font-mono text-[11px] text-heritage-grey">{ref}</span>
                        </div>
                        <div className="mt-1 text-[12px] text-heritage-grey">
                          {heatNumber ? `Heat ${heatNumber} - ` : ''}{reason}
                        </div>
                      </div>
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold uppercase text-amber-700">{statusText}</span>
                    </button>
                  );
                }) : (
                  <div className="px-4 py-8 text-center text-[13px] text-heritage-grey">No pending review items for this project.</div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-[#E2E5EA] bg-white shadow-sm">
              <div className="border-b border-[#E2E5EA] px-4 py-3">
                <h4 className="text-[14px] font-semibold text-blueprint-blue">Other tools</h4>
              </div>
              <div className="divide-y divide-[#EEF0F3]">
                {[
                  { label: 'Heats & lots', description: `${overviewStats.total} material items`, icon: Boxes, onClick: () => setIsHeatLotViewOpen(true) },
                  { label: 'Ingestion', description: `${overviewStats.total} files in queue`, icon: Upload, onClick: () => setIsUploadDialogOpen(true) },
                  { label: 'Completeness', description: `${overviewStats.pending} incomplete pipes`, icon: Activity, onClick: () => setIsPipeDashboardOpen(true) },
                  { label: 'Project limits', description: 'Report overrides', icon: Settings, onClick: () => setIsProjectLimitsModalOpen(true) },
                ].map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      key={tool.label}
                      type="button"
                      onClick={tool.onClick}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[#F7F8FA]"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F4F6F8]">
                        <Icon className="h-4 w-4 text-heritage-grey" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-blueprint-blue">{tool.label}</span>
                        <span className="block text-[12px] text-heritage-grey">{tool.description}</span>
                      </span>
                      <span className="text-[18px] text-heritage-grey">{'>'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )
    },
    pendingReview: {
      label: 'Pending Review',
      icon: Activity,
      content: () => (
        <div className="space-y-5 bg-[#F7F8FA] p-4 sm:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-heritage-grey">
                Material Traceability
              </div>
              <h3 className="mt-1 text-[22px] font-semibold text-blueprint-blue">Pending review queue</h3>
              <p className="mt-1 text-[13px] text-heritage-grey">
                {pendingReviewQueue.length} items awaiting adjudication
                {pendingReviewQueue[0]?.updatedAt ? ` - oldest ${formatPendingWaiting(pendingReviewQueue[pendingReviewQueue.length - 1]?.updatedAt)}` : ''}
              </p>
            </div>
            <Button type="button" variant="outline" className="border-[#E2E5EA] bg-white text-blueprint-blue" onClick={refreshProjectOverview}>
              Refresh
            </Button>
          </div>

          <div className="rounded-xl border border-[#E2E5EA] bg-white shadow-sm">
            <div className="overflow-x-auto">
              <Table className="min-w-[980px]">
                <TableHeader>
                  <TableRow className="bg-white">
                    <TableHead className="w-[54px]"></TableHead>
                    <TableHead>Pipe</TableHead>
                    <TableHead>Report</TableHead>
                    <TableHead>Field / Reason</TableHead>
                    <TableHead>Measured</TableHead>
                    <TableHead>Limit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Waiting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingReviewQueue.length > 0 ? pendingReviewQueue.map((item: any) => {
                    const material = item.material || item;
                    const details = material.details || {};
                    const categoryKey = (item.categoryKey || item.key || 'overview') as MaterialTabKey;
                    const reportType = item.reportType || item.report_type || material.reportType || '-';
                    const heatNumber = item.heatNumber || material.heatNumber || details.pipeHeatNumber;
                    const pipeRef = details.pipeNo || details.pipeNRI || item.pipeNo || item.pipeNumber || material.pipeNo || material.materialId || '-';
                    const reason = item.reason || item.fieldLabel || item.fieldPath || material.materialStatus || material.status || 'Pending review';
                    const measured = item.measuredValue || item.measured || item.value || '-';
                    const limit = item.limit || item.limitText || item.projectLimit || '-';
                    const statusText = String(item.overallStatus || material.materialStatus || material.status || item.status || 'Pending review').replace(/_/g, ' ');

                    return (
                      <TableRow
                        key={`${item.id || pipeRef}-${reportType}-${heatNumber || ''}`}
                        className="cursor-pointer hover:bg-[#F7F8FA]"
                        onClick={() => openPendingReviewItem(item, categoryKey)}
                      >
                        <TableCell>
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-700">
                            !
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-[12px] font-semibold text-blueprint-blue">{pipeRef}</TableCell>
                        <TableCell className="font-mono text-[12px] font-semibold text-text-grey">{String(reportType).replace(/_/g, ' ')}</TableCell>
                        <TableCell>
                          <div className="text-[12px] font-medium text-blueprint-blue">{reason}</div>
                          <div className="mt-1 text-[11px] text-heritage-grey">{heatNumber ? `Heat ${heatNumber}` : 'Project scope'}</div>
                        </TableCell>
                        <TableCell className="font-mono text-[12px] text-text-grey">{measured}</TableCell>
                        <TableCell className="font-mono text-[12px] text-heritage-grey">{limit}</TableCell>
                        <TableCell>
                          <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold uppercase text-amber-700">{statusText}</span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-[12px] text-text-grey">{formatPendingWaiting(item.updatedAt || item.updated_at)}</TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-[13px] text-heritage-grey">
                        No pending review items for this project.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )
    },
    pipe: {
      label: pipeSizesData[selectedPipeSize]?.projectType === 'pipeline' ? 'Pipes' : pipeSizesData[selectedPipeSize]?.projectType === 'fabrication' ? 'Base Pipes' : 'Others',
      icon: FileText,
      content: () => {

        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading pipe data...</span>
              </div>
            </div>
          ) : (
            <PipeTab
              materialCategory="Pipe"
              materials={getMaterials('pipe')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              unit={unit}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
              project={project.project}
              isPipemillproject={isPipemillproject}
              onReportsChanged={refreshProjectOverview}
            />
          )
        );
      }
    },
    Bends: {
      label: 'Bends',
      icon: FileText,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading bends data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Bends"
            materials={getMaterials('bends')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    Flanges: {
      label: 'Flanges',
      icon: FileText,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading flanges data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Flanges"
            materials={getMaterials('flanges')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    hydro: {
      label: 'Hydro',
      icon: Droplets,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading hydro data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Hydro"
            materials={getMaterials('hydro')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    torquing: {
      label: 'Torquing',
      icon: Wrench,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading torquing data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Torquing"
            materials={getMaterials('torquing')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    gauging: {
      label: 'Gauging',
      icon: Gauge,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading gauging data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Gauging"
            materials={getMaterials('gauging')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    flushing: {
      label: 'Flushing',
      icon: Activity,
      content: () => (
        tableDataLoading ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
              <span className="text-heritage-grey text-lg">Loading flushing data...</span>
            </div>
          </div>
        ) : (
          <PipeTab
            materialCategory="Flushing"
            materials={getMaterials('flushing')}
            selectedPipeSize={selectedPipeSize}
            currentPage={currentPage}
            materialsPerPage={materialsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            unit={unit}
            refreshMaterials={refreshMaterials}
            onRowDelete={handleRowDelete}
            deletingRows={deletingRows}
            documentTags={documentTags}
            setDocumentTags={setDocumentTags}
            pipeSizesData={pipeSizesData[selectedPipeSize]}
            canEdit={canEdit}
            onOpenViewer={openViewer}
            project={project.project}
          />
        )
      ),
    },
    forge: {
      label: 'Forging',
      icon: Wrench,
      content: () => {

        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading forge data...</span>
              </div>
            </div>
          ) : (
            <ForgeTab
              materials={getMaterials('forge')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
            />
          ))
      }
    },
    gas: {
      label: 'Gas',
      icon: Flame,
      content: () => {

        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading gas data...</span>
              </div>
            </div>
          ) : (
            <GasTab
              materials={getMaterials('gas')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
            />
          ))
      }
    },
    wire: {
      label: 'Wire Lot',
      icon: Cable,
      content: () => {

        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading wire data...</span>
              </div>
            </div>
          ) : (
            <WireTab
              materials={getMaterials('wire')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
            />
          ))
      }
    },
    coating: {
      label: 'Coating',
      icon: Cable,
      content: () => {

        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading coating data...</span>
              </div>
            </div>
          ) : (
            <CoatingTab
              materials={getMaterials('coating')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
            />
          ))
      }
    },
    misc: {
      label: 'Misc',
      icon: Boxes,
      content: () => {
        return (
          tableDataLoading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e04530]"></div>
                <span className="text-gray-600 text-lg">Loading miscellaneous materials...</span>
              </div>
            </div>
          ) : (
            <MiscTab
              materials={getMaterials('misc')}
              selectedPipeSize={selectedPipeSize}
              currentPage={currentPage}
              materialsPerPage={materialsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              refreshMaterials={refreshMaterials}
              onRowDelete={handleRowDelete}
              deletingRows={deletingRows}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
              pipeSizesData={pipeSizesData[selectedPipeSize]}
              canEdit={canEdit}
              onOpenViewer={openViewer}
            />
          )
        );
      }
    },
    // nde: {
    //   label: 'NDE',
    //   icon: Package,
    //   content: () => {
    //     return (
    //       tableDataLoading ? (
    //         <div className="flex items-center justify-center min-h-[700px]">
    //           <div className="flex items-center space-x-3">
    //             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
    //             <span className="text-gray-600 text-lg">Loading NDE data...</span>
    //           </div>
    //         </div>
    //       ) : (
    //         <NDEConsumableTable 
    //           materials={getMaterials('nde')}
    //           selectedPipeSize={selectedPipeSize}
    //           currentPage={currentPage}
    //           materialsPerPage={materialsPerPage}
    //           onPageChange={handlePageChange}
    //           onItemsPerPageChange={handleItemsPerPageChange}
    //           refreshMaterials={refreshMaterials}
    //           onRowDelete={handleRowDelete}
    //           deletingRows={deletingRows}
    //           documentTags={documentTags}
    //           setDocumentTags={setDocumentTags}
    //           pipeSizesData={pipeSizesData[selectedPipeSize]}
    //           canEdit={canEdit}
    //           onOpenViewer={openViewer}
    //         />
    //       )
    //     );
    //   }
    // }
  } as const), [materials, selectedPipe, selectedPipeSize, currentPage, materialsPerPage, handlePageChange, setupData, handleRowDelete, deletingRows, materialOverviewCategories, overviewStats, pendingReviewItems, pendingReviewQueue, isProjectOverviewLoading, openPendingReviewItem, refreshProjectOverview]);

   // Check if there are any specifications for this subtype
   const hasAnyPipes = !isLoadingData && pipeSizesData.length > 0;
 // Check if there are any specifications for this subtype
  //If no pipes exist for this subtype, show message to create pipes first
  if (!hasAnyPipes && !isLoadingData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-purple-800">
       
        <h3 className="text-xl font-semibold">Material Traceability</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Cable className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pipes created yet</h3>
          <p className="text-gray-500 max-w-sm">
            You need to create pipes in the Setup section first before adding specifications. Go to Setup ΓåÆ Pipeline to configure your pipes.
          </p>
        </div>
      </div>
    );
  }

  


  return (
    <>
      {
        isLoadingData ? (
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading materials data...</span>
            </div>
          </div>
        ) : (
          <>
            <Card className="border-none shadow-none">
              <CardContent className="p-6">
                {materialTab !== 'overview' && materialTab !== 'pendingReview' && (
                  <>
                    {/* Pipe Size + Actions on one row */}
                    <div className="flex items-center gap-3 flex-wrap mb-6">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Label className="text-[12px] font-medium text-text-grey whitespace-nowrap">Pipe Size:</Label>
                        <PipeSelector
                          value={selectedPipeSize.toString()}
                          onChange={(value: string) => {
                            const newPipeSizeIndex = Number(value);
                            setSelectedSize(value);
                            setSelectedPipeSize(newPipeSizeIndex >= 0 ? newPipeSizeIndex : selectedPipeSize);
                            setCurrentPage(1);

                            if (pipeSizesData && pipeSizesData[newPipeSizeIndex]) {
                              setSelectedPipe(pipeSizesData[newPipeSizeIndex]);
                              setTableDataLoading(true);
                            }
                          }}
                          placeholder="Select pipe size"
                          options={(pipeSizesData || []).map((pipe, index) => ({
                            value: index.toString(),
                            label: pipe.displayName,
                          }))}
                        />
                      </div>
                      {selectedPipe && selectedPipe.materialTraceability && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {canEdit && (
                            <Button
                              onClick={() => setIsAddingMaterial(true)}
                              size="sm"
                              className="bg-hero-red text-white hover:bg-hero-red-60 flex items-center gap-2 justify-center"
                            >
                              <Plus className="h-4 w-4" />
                              Add Material
                            </Button>
                          )}
                          {(canEdit || canReport) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors">
                                <MoreHorizontal className="h-4 w-4 text-text-grey" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit && (
                                  <DropdownMenuItem onClick={() => setIsUploadBatchMaterialDialogOpen(true)}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Batch Documents
                                  </DropdownMenuItem>
                                )}
                                {canEdit && (
                                  <DropdownMenuItem onClick={handleDownloadTemplate}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Template
                                  </DropdownMenuItem>
                                )}
                                {canEdit && (
                                  <DropdownMenuItem onClick={() => setIsUploadDialogOpen(true)}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Upload Materials
                                  </DropdownMenuItem>
                                )}
                                {canReport && (
                                  <DropdownMenuItem onClick={() => handleGenerateMaterialReport()}>
                                    <FileText className="h-4 w-4 mr-2" />
                                    Reports
                                  </DropdownMenuItem>
                                )}
                                {canReport && (
                                  <DropdownMenuItem onClick={handleExport}>
                                    <FileText className="h-4 w-4 mr-2" />
                                    Generate Report
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setIsProjectLimitsModalOpen(true)}>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Project Limits
                                </DropdownMenuItem>

                                <DropdownMenuItem onClick={() => setIsHeatLotViewOpen(true)}>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Heat & Lot
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setIsPipeDashboardOpen(true)}>
                                  <Activity className="h-4 w-4 mr-2" />
                                  Dashboard
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Material Category Tabs */}
                {selectedPipe && selectedPipe?.materialTraceability ? <Tabs className="w-full"
                  value={materialTab}
                  onValueChange={(value) => setMaterialTab(value as MaterialTabKey)}
                >
                  <div className="sticky top-0 bg-white z-10 border-b-2 border-[#E2E5EA] flex items-end">
                    <TabsList className="h-auto justify-start gap-8 bg-transparent px-0 py-0">
                      {Object.entries(TABS_CONFIG).map(([key, config]) => {
                        const Icon = config.icon;
                        if (!isMaterialTabVisible(key as MaterialTabKey)) {
                          return null;
                        }

                        return (
                          <TabsTrigger
                            key={key}
                            value={key}
                            className="relative inline-flex items-center border-b-2 border-transparent -mb-[2px] px-1 pb-3 pt-2 text-[13px] font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-blueprint-blue transition-colors duration-150"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {config.label}
                            </div>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </div>
                  {Object.entries(TABS_CONFIG).map(([key, config]) => {
                    if (!isMaterialTabVisible(key as MaterialTabKey)) {
                      return null;
                    }

                    return (
                      <TabsContent key={key} value={key}>
                        {config.content()}
                      </TabsContent>
                    );
                  })}
                </Tabs> : <div className="flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center justify-center  text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6 shadow-sm border border-gray-100">
                      <Package className="h-10 w-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight">
                      Material Traceability Not Selected
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Material traceability is not enabled for this project type.
                    </p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      This feature allows you to track materials throughout the project lifecycle for quality assurance and compliance.
                    </p>
                  </div>
                </div>}

                <AddMaterialDialog
                  isOpen={isAddingMaterial}
                  onClose={() => setIsAddingMaterial(false)}
                  onAdd={handleAddMaterial}
                  selectedPipeSize={selectedPipeSize}
                  project={project}
                  pipeSizesData={pipeSizesData}
                  unit={unit}
                  setupData={setupData}
                  documentTags={documentTags}
                  setDocumentTags={setDocumentTags}
                />
              </CardContent>
            </Card>

            <UploadMaterialsDialog
              isOpen={isUploadDialogOpen}
              onClose={() => setIsUploadDialogOpen(false)}
              onUploadComplete={handleUploadComplete}
              setupData={getFilteredSetupData()}
              project={project}
              pipeSizesData={pipeSizesData}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
            />

          {isUploadBatchMaterialDialogOpen && <UploadBatchMaterialDocumentModal
              isOpen={isUploadBatchMaterialDialogOpen}
              onClose={() => setIsUploadBatchMaterialDialogOpen(false)}
              onUploadComplete={handleUploadComplete}
              materials={[...getMaterials('pipe'), ...getMaterials('forge')]}
              selectedPipeSize={pipeSizesData[selectedPipeSize]}
              documentTags={documentTags}
              setDocumentTags={setDocumentTags}
            />}

          {isReportModalOpen && <MaterialReportModal
              isOpen={isReportModalOpen}
              onClose={() => setIsReportModalOpen(false)}
              projectId={project.project.projectId}
              projectName={project.project.projectName}
              pipeSizesData={pipeSizesData}
              selectedTabName={TABS_CONFIG[materialTab as keyof typeof TABS_CONFIG]?.label || 'Material'}
              reportType={materialTab}
              isCustomerRepresentativeUser={isCustomerRepresentativeUser}
              isQCInspectorUser={isQCInspectorUser}
              isWelderUser={isWelderUser}
              currentUserTeamMemberId={currentUserTeamMemberId}
              canSign={canReport}
              canEdit={canEdit}
              isAdminOrSuperAdmin={isAdminOrSuperAdmin}
            />
          }
          {isReportsViewModalOpen && <MaterialReportsViewModal
              isOpen={isReportsViewModalOpen}
              onClose={() => setIsReportsViewModalOpen(false)}
              projectId={project.project.projectId}
              projectName={project.project.projectName}
              pipeSizesData={pipeSizesData}
            />
          }

          <ProjectLimitsModal
            isOpen={isProjectLimitsModalOpen}
            onOpenChange={setIsProjectLimitsModalOpen}
            projectId={Number(project.project.projectId) || 0}
            selectedReportType="CHEMICAL"
            metadataByType={{}}
            selectedPipe={selectedPipe}
            details={selectedPipe || setupData}
            selectedMaterial={null}
          />

          <HeatLotViewModal
            isOpen={isHeatLotViewOpen}
            onOpenChange={setIsHeatLotViewOpen}
            materials={[]}
            heatLotItems={heatLotItems}
            isLoading={isHeatLotLoading}
            selectedPipe={selectedPipe}
            onOpenPipeReport={openDashboardPipeReport}
          />

          <PipeDashboardModal
            isOpen={isPipeDashboardOpen}
            onOpenChange={setIsPipeDashboardOpen}
            materials={[]}
            dashboardItems={pipeDashboardItems}
            frequencyCoverage={pipeDashboardFrequencyCoverage}
            isLoading={isPipeDashboardLoading}
            selectedPipe={selectedPipe}
            onOpenPipeReport={openDashboardPipeReport}
          />

          {isDashboardReportOpen && dashboardReportMaterial && (
            <PipeMaterialReportModal
              isOpen={isDashboardReportOpen}
              onClose={() => {
                setIsDashboardReportOpen(false);
                setDashboardReportMaterial(null);
                setDashboardInitialReportType(null);
              }}
              selectedMaterial={dashboardReportMaterial}
              selectedPipe={selectedPipe}
              unit={unit}
              onReportsChanged={refreshProjectOverview}
              initialReportType={dashboardInitialReportType}
            />
          )}

            {/* PDF Viewer */}
            {viewerType === 'pdf' && viewerFile && (
              <PdfViewer
                fileUrl={viewerFile.fileUrl}
                fileName={viewerFile.fileName}
                onClose={closeViewer}
              />
            )}

            {/* Media Viewer */}
            {viewerType === 'media' && viewerFiles.length > 0 && (
              <MediaViewer
                files={viewerFiles}
                initialIndex={0}
                onClose={closeViewer}
              />
            )}

            {/* Document Viewer */}
            {viewerType === 'document' && viewerFile && (
              <DocumentViewer
                document={viewerFile}
                onClose={closeViewer}
              />
            )}
          </>
        )
      }

    </>

  );

}; 
